import WebsiteBehaviors from "../website-behaviors/index.js";
import {waitUntil} from "../utils/async.js";

const PN_CANVAS_PERMALINK_RESPONSE = "PN_CANVAS_PERMALINK_RESPONSE";
const PN_CANVAS_PERMALINK_REQUEST = "PN_CANVAS_PERMALINK_REQUEST";

/**
 * Canvas encompasses a number of different scenarios where an embedded pdf viewer is used.
 * This comment block details/defines the types we're aware of so far.
 *
 * 1: Embedded file viewer "Type A"
 *	Top frame:
 *	https://canvas.instructure.com/courses/1471407/files?preview=71227900
 *	"view" frame:
 *	https://canvadocs.instructure.com/1/sessions/eyJhb…xRXf1XU-MeexDK5SjSLfiiMU4w6tdvZ0g/view
 *
 * 2: "Commons" viewer accessed from file index ("Type B")
 *	Top frame: (url doesn't vary by asset)
 *	https://canvas.instructure.com/accounts/10/external_tools/69910?launch_type=global_navigation
 *	"tool_content" frame: (contains permalink button)
 *	https://lor.instructure.com/resources/02d24bf582954bdbb6e481a22d5112cb
 *	"view" frame:
 *	https://canvadocs-lor.instructure.com/1/sessions/eyJhb…xRXf1XU-MeexDK5SjSLfiiMU4w6tdvZ0g/view
 *
 *	See #3 for the permalink we get from the "tool_content" frame here.
 *
 * 3: Commons content accessed from permalink ("Type C")
 *	Top frame: (contains permalink button)
 *	https://lor.instructure.com/resources/02d24bf582954bdbb6e481a22d5112cb?shared
 *	"view" frame:
 *	https://canvadocs-lor.instructure.com/1/sessions/eyJhb…xRXf1XU-MeexDK5SjSLfiiMU4w6tdvZ0g/view
 *
 * 4: Commons content accessed from lor.instructure.com ("Type D")
 *	This differs from "Type C" in that the "?shared" query in the url is missing, so
 *	we need to be sure to grab the permalink (which includes the query string).
 *	You can get to this type of links by going to https://lor.instructure.com/shared and picking a document.
 *	Top frame: (contains permalink button)
 *	https://lor.instructure.com/resources/02d24bf582954bdbb6e481a22d5112cb
 *	"view" frame:
 *	https://canvadocs-lor.instructure.com/1/sessions/eyJhb…xRXf1XU-MeexDK5SjSLfiiMU4w6tdvZ0g/view
 *
 * 5: From... another version of canvas? ("Type E")
 *	Top frame:
 *	https://ilearn.laccd.edu/courses/85910/files/7437120?module_item_id=3155870
 *	"view" frame:
 *	https://canvadocs.instructure.com/1/sessions/eyJhb…xRXf1XU-MeexDK5SjSLfiiMU4w6tdvZ0g/view
 */
class Canvas extends WebsiteBehaviors {
	identifier() {
		return "Canvas";
	}

	// @ts-expect-error TS(7006) FIXME: Parameter 'location' implicitly has an 'any' type.
	static test(location) {
		// Cases where the "viewer" frame is a direct child of the top frame.
		// ("Type A" and "Type E")
		if (location.host === "canvadocs.instructure.com" || location.host === "canvadocs-lor.instructure.com")
			return true;

		if (location.host === "lor.instructure.com" && window.name === "tool_content") {
			// "Type B" "tool_content" frame in Commons.
			// Or "Type C" permalink view.
			// We'll need to grab the permalink from this frame.
			return true;
		}

		if (location.host === "lor.instructure.com" && location.pathname.startsWith("/resources/")) {
			// "Type D" top-level frame in Commons.
			// We'll need to grab the permalink and document name from this frame.
			return true;
		}

		// For the rest of the tests, make sure we're on a domain that we think is canvas.
		// Ex: canvas.upenn.edu, utk.instructure.com
		if (this.isCanvasDomain(location.host)) {
			if (document.getElementById("tool_content") && document.querySelector("header.ic-app-header")) {
				// "Type B" top-level frame with a "tool_content" subframe that probably has a permalink we care about.
				// Seems to be a canvas site. We detect it this way because they
				// might be on many different domains, depending on the school.
				// We need to run the topFrameHandler() to coordinate getting the permalink to the right place.
				// The iframe has both name and id "tool_content", but in firefox we can't seem to see the frame
				// when we refer to it by window.frames["tool_content"], so we just check for the iframe itself in the dom,
				// with its id ("tool_content").
				return true;
			}

			if (location.pathname.startsWith("/courses/") && location.pathname.endsWith("/files") && document.querySelector("header.ic-app-header")) {
				// "Type A" top-level frame. We need to run topFrameHandler() to get the document name.
				return true;
			}
		}

		return false;
	}

	// @ts-expect-error TS(7006) FIXME: Parameter 'host' implicitly has an 'any' type.
	static isCanvasDomain(host) {
		return (host.startsWith("canvas.") || host.endsWith(".instructure.com"));
	}

	// @ts-expect-error TS(2416) FIXME: Property 'topFrameHandler' in type 'Canvas' is not... Remove this comment to see the full error message
	async topFrameHandler() {
		// Canvas "Commons" uses a special "Copy Resource Link" button
		// in the top frame OR the "tool_content" frame (depending on where we are)
		// for getting a permalink. See the link/frame examples toward the top of this file.

		if (document.getElementById("tool_content")) {
			// We're in a Commons session and have an embedded frame containing the permalink,
			// so we need to request the pageData from that frame.
			// The following illustrates all of the message-passing that occurs:
			//	Where:
			//		PDF "view" frame is canvasdocs-lor.instructure.com
			//		Top frame is canvas.instructure.com
			//		"tool_content" frame is lor.instructure.com
			//
			//	PDF "view" frame      main.js   Sends FRAME_REPLACE message
			//	Top frame             main.js   Receives FRAME_REPLACE and calls topFrameHandler()
			//	Top frame             Canvas.js Sets up listener for PN_CANVAS_PERMALINK_RESPONSE,
			//	                                sends PN_CANVAS_PERMALINK_REQUEST to tool_content frame.
			//	"tool_content" frame  Canvas.js Receives PN_CANVAS_PARMALINK_REQUEST, finds permalink,
			//	                                Sends PN_CANVAS_PERMALINK_RESPONSE to top frame.
			//	Top frame             Canvas.js Receives PN_CANVAS_PERMALINK_RESPONSE in topFrameHandler(),
			//	                                then topFrameHandler() returns data to main.js.
			//	Top Frame             main.js   Receives result from topFrameHandler(),
			//	                                sends FRAME_REPLACE_TOP_URL to all subframes,
			//	All frames            main.js   Forwards FRAME_REPLACE_TOP_URL to all subframes,
			//	                                Updates local copy of page data based on passed data.

			// @ts-expect-error TS(7034) FIXME: Variable 'pageData' implicitly has type 'any' in s... Remove this comment to see the full error message
			let pageData;
			// @ts-expect-error TS(7034) FIXME: Variable 'cleanup' implicitly has type 'any' in so... Remove this comment to see the full error message
			// eslint-disable-next-line prefer-const
			let cleanup;
			// @ts-expect-error TS(7006) FIXME: Parameter 'evt' implicitly has an 'any' type.
			const msgHandler = function (evt) {
				if (evt.data.type === PN_CANVAS_PERMALINK_RESPONSE) {
					// @ts-expect-error TS(7005) FIXME: Variable 'pageData' implicitly has an 'any' type.
					if (!pageData) {
						// make sure we only grab the result once
						pageData = evt.data.pageData;
						// @ts-expect-error TS(7005) FIXME: Variable 'cleanup' implicitly has an 'any' type.
						if (cleanup)
							cleanup();
					}
				}
			};
			cleanup = function () {
				window.removeEventListener("message", msgHandler);
			};
			window.addEventListener("message", msgHandler);

			const stopCondition = () => {
				// Send permalink request each time we check for completion (every 300ms by default)
				// In chrome we can just do window.frames['tool_content'].postMessage(...),
				// but we can't seem to access it by frame name in firefox, so we need to
				// use the iframe's id to get the dom node, then use its contentWindow
				// in order to send the message to the appropriate frame.
				(document.getElementById("tool_content") as any).contentWindow.postMessage({
					type: PN_CANVAS_PERMALINK_REQUEST
				}, "*");

				// Stop when pageData is populated
				// @ts-expect-error TS(7005) FIXME: Variable 'pageData' implicitly has an 'any' type.
				return !!pageData;
			};
			try {
				await waitUntil(stopCondition);
				cleanup();
				return pageData;
			} catch (e) {
				// Not really much we can do. pageData will just be from the top frame.
				return null;
			}
		} else if (window.location.pathname.startsWith("/courses/") && window.location.pathname.endsWith("/files")) {
			// "Type A" top-level frame. We need to grab the document name.

			const docTitleElement = document.querySelector("h1.ef-file-preview-header-filename");
			if (!docTitleElement)
				return null; const pageTitle = (docTitleElement as any).innerText;
			// We'll just ignore proxy support for now. It shouldn't be needed for canvas sites....
			const pageUrl = window.location.href;

			return {
				pageData: {
					pageTitle,
					pageUrl
				}
			};
		} else if (window.location.pathname.startsWith("/resources/")) {
			// "Type D" top-level frame. We need to grab the permalink and document name.
			// We also end up here in the "Type C" situation.

			return this._getPageData();
		} else {
			// We're on a normal page and expect the top frame url to be used for the pageData.
			return null;
		}
	}

	shouldFrameTakeControl() {
		// There are two cases we could be in:
		// 1) A "tool_content" frame where we need to set up a handler for returning the permalink when queried.
		// 2) A viewer frame where we need to "take control" and embed the extension into the page.

		if (window.top !== window.self && window.name === "tool_content") {
			// 1) tool_content frame, probably in Commons.

			// Set up a message response so the top frame can query us for the permalink.
			window.addEventListener("message", (evt) => {
				if (evt.data.type === PN_CANVAS_PERMALINK_REQUEST) {
					const pageData = this._getPageData();
					if (!pageData)
						return;

					// @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
					window.top.postMessage({
						type: PN_CANVAS_PERMALINK_RESPONSE,
						pageData
					}, "*");
				}
			});

			// Don't need to take control in this frame.
			return false;
		} else {
			// 2) If we're not in the embedded "tool_content" frame, we probably want the current frame to take control.
			return true;
		}
	}

	/**
	 * For "Type B" and "Type C":
	 * Grab the permalink data from the page (on Commons resource pages).
	 * This is only useful if we're in the "tool_content" frame
	 * (or its top-level counterpart with essentially the same url).
	 * Return null if we can't find the permalink or document name.
	 *
	 * @return {Object|null}
	 */
	_getPageData() {
		const resourceLinkElement = document.querySelector("textarea.lor-copy-resource-link-btn-link");
		if (resourceLinkElement) {
			const pageUrl = (resourceLinkElement as any).value;

			// On the Commons pages, the resource name is in the .lor-course-title h1 tag.
			const courseTitleElement = document.querySelector("h1.lor-course-title");
			let pageTitle;
			if (courseTitleElement)
				pageTitle = courseTitleElement.textContent;

			if (!pageUrl || !pageTitle)
				return null;

			return {
				pageData: {
					pageTitle,
					pageUrl
				}
			};
		} else {
			// We don't seem to have a special resource link to return.
			return null;
		}
	}
}

export default Canvas;
